import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, doc, updateDoc, addDoc, deleteDoc, query, where, orderBy, getDoc, getDocs, limit } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Promotion } from '../models/promotion.models';

/**
 * Service promotions frontoffice.
 *
 * #428 — les calculateurs de remise client-side divergents ont été
 * SUPPRIMÉS (calculateDiscount / validatePromotionAgainstOrder /
 * getAllApplicablePromotions / getEligiblePromotions / getPromotionByCode /
 * usePromotion : ~350 lignes, aucun appelant money-path). La seule source de
 * vérité du calcul de remise est le SERVEUR :
 *   - préview au checkout  → CF `validatePromoCode` (ce service, ci-dessous),
 *   - montant facturé      → `createInvoice` (même PromotionValidationService).
 * Les fronts n'affichent que le résultat serveur, ils ne recalculent jamais
 * une remise. Ce qui reste ici = lecteurs Firestore (affichage/CRUD admin) +
 * la préview serveur.
 */
@Injectable({
  providedIn: 'root',
})
export class PromotionService {
  constructor(private firestore: Firestore, private functions: Functions) {}

  getPromotions(): Observable<Promotion[]> {
    return collectionData(collection(this.firestore, 'promotions'), { idField: 'id' }) as Observable<Promotion[]>;
  }

  getActivePromotions(): Observable<Promotion[]> {
    const q = query(collection(this.firestore, 'promotions'), where('status', '==', 'active'));
    return collectionData(q, { idField: 'id' }) as Observable<Promotion[]>;
  }

  // 🆕 Get promotions for homepage display (Phase 1)
  getHomepagePromotions(): Observable<Promotion[]> {
    const q = query(
      collection(this.firestore, 'promotions'),
      where('status', '==', 'active'),
      where('isPublic', '==', true),
      where('displayOnHomepage', '==', true),
      orderBy('displayOrder', 'asc')
    );
    return (collectionData(q, { idField: 'id' }) as Observable<Promotion[]>).pipe(
      map((promotions) => {
        // Filter by date range
        const today = new Date();
        return promotions.filter((promo) => {
          const startDate = new Date(promo.startDate);
          const endDate = new Date(promo.endDate);
          return today >= startDate && today <= endDate;
        });
      })
    );
  }

  // 🆕 Get promotions to suggest at checkout (Phase 1)
  getCheckoutPromotions(orderData?: any): Observable<Promotion[]> {
    const q = query(
      collection(this.firestore, 'promotions'),
      where('status', '==', 'active'),
      where('isPublic', '==', true),
      where('displayInCheckout', '==', true),
      orderBy('priority', 'desc')
    );
    return (collectionData(q, { idField: 'id' }) as Observable<Promotion[]>).pipe(
        map((promotions) => {
          // Filter by date range and eligibility
          const today = new Date();
          return promotions.filter((promo) => {
            // 🎯 Exclure les promos CIBLÉES de l'auto-affichage checkout : un code
            // avec targetedUsers n'est PAS une offre générale — il ne doit jamais
            // être pré-rempli/auto-appliqué pour un utilisateur non ciblé (fuite
            // du code privé + UX trompeuse ; le débit était déjà protégé par la
            // validation serveur fail-closed #426, mais le code ne doit pas fuiter).
            // Idem firstOrderOnly (offre conditionnelle, pas générale).
            const targeted = (promo as any).targetedUsers;
            if (Array.isArray(targeted) && targeted.length > 0) {
              return false;
            }
            if ((promo as any).firstOrderOnly === true) {
              return false;
            }

            const startDate = new Date(promo.startDate);
            const endDate = new Date(promo.endDate);

            // Date check
            if (today < startDate || today > endDate) {
              return false;
            }

            // If orderData provided, check eligibility
            if (orderData) {
              // Country check
              if ((promo as any).countriesAllowed && (promo as any).countriesAllowed.length > 0) {
                if (!orderData.phonePrefix2 || !(promo as any).countriesAllowed.includes(orderData.phonePrefix2)) {
                  return false;
                }
              }

              // Amount check
              if (promo.minAmount > 0 && orderData.cost < promo.minAmount) {
                return false;
              }
              if (promo.maxAmount > 0 && orderData.cost > promo.maxAmount) {
                return false;
              }
            }

            return true;
          });
        })
      );
  }

  getPromotion(id: string): Observable<Promotion | null> {
    return from(getDoc(doc(this.firestore, 'promotions', id))).pipe(
      map(docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          return data ? { id: docSnap.id, ...data } as Promotion : null;
        } else {
          return null;
        }
      }),
      catchError(() => of(null))
    );
  }

  async createPromotion(promotion: Promotion): Promise<string> {
    // #429 — unicité de code (même garde que marketing.service côté
    // backoffice) : deux docs même code = application non déterministe.
    if (promotion.code) {
      const dup = await getDocs(query(
        collection(this.firestore, 'promotions'),
        where('code', '==', promotion.code),
        limit(1)
      ));
      if (!dup.empty) {
        throw new Error(`Le code « ${promotion.code} » existe déjà`);
      }
    }
    const docRef = await addDoc(collection(this.firestore, 'promotions'), promotion);
    return docRef.id;
  }

  updatePromotion(id: string, promotion: Partial<Promotion>): Promise<void> {
    return updateDoc(doc(this.firestore, 'promotions', id), promotion);
  }

  deletePromotion(id: string): Promise<void> {
    return deleteDoc(doc(this.firestore, 'promotions', id));
  }

  /**
   * Préview d'un code promo — validation SERVEUR via la CF `validatePromoCode`
   * (#425/#428). Remplace l'ancienne lecture Firestore directe + recalcul
   * client (algorithme divergent du serveur) : ce que la préview affiche est
   * exactement ce que `createInvoice` appliquera. Nécessaire aussi car les
   * rules `promotions` n'autorisent plus la recherche client d'un code
   * non-public (avoirs CREDIT-* ciblés).
   */
  validatePromoCode(code: string, orderData: any): Observable<{
    valid: boolean;
    discount: number;
    promotionId?: string;
    title?: string;
    discountType?: string;
    errorMessage?: string;
  }> {
    const callable = httpsCallable<any, {
      valid: boolean;
      discount: number;
      finalAmount: number;
      promotionId: string | null;
      discountType: string | null;
      errorMessage: string | null;
    }>(this.functions, 'validatePromoCode');

    const payload = {
      code,
      orderAmount: Number(orderData?.cost) || 0,
      weight: Number(orderData?.weight) || undefined,
      // 'vm_shipping' (front VM) → 'vm' (vocabulaire serveur) ; les autres
      // flows ne connaissent pas leur serviceType à ce stade → skip serveur.
      serviceType: orderData?.type === 'vm_shipping' ? 'vm' : orderData?.serviceType,
    };

    return from(callable(payload)).pipe(
      map(({ data }) => ({
        valid: data.valid,
        discount: data.discount,
        promotionId: data.promotionId ?? undefined,
        discountType: data.discountType ?? undefined,
        errorMessage: data.errorMessage ?? undefined,
      })),
      catchError((error) => {
        console.error('❌ [PROMO] Error validating code:', error);
        return of({
          valid: false,
          discount: 0,
          errorMessage: 'Erreur lors de la validation du code'
        });
      })
    );
  }
}
